import { brandsI18n } from '../../localization'

// Fallback TNC URI for Axis
const AXIS_TNC_URI =
  'https://drive.google.com/viewerng/viewer?embedded=true&url=https://www.axisbank.com/docs/default-source/default-document-library/mitc-credit-cards.pdf'

// Interface for incoming props to the OTP Verify Screen
interface OTPVerifyScreenProps {
  error: string | null
  layout: {
    title?: string
    description?: string
    tncContent: {
      controlled?: boolean
      text?: string
      linkTitle?: string
      tncURI?: string
      privacyURI?: string
      privacyTitle?: string
    }
    otpLength?: number
    phoneNumber?: number
    resend?: {
      enabled?: boolean
      title?: string
      durationInSec?: number
      resendCount?: number
    }
    retryCount?: number
  }
  // Optional metadata object for additional properties
  metadata?: {
    showTnC: boolean
    phoneNumber?: number
    otpLength?: number
    tncUri?: string
    remainingAttempts?: number
    privacyURI?: string
  }
  maskNumber?: boolean
  hideTnC?: boolean
}

// Interface for the transformed props to be used by the OTP Verify Screen
interface OTPTransformedProps {
  title: string
  description: string
  tncContent: {
    showTnC: boolean
    controlled?: boolean
    text: string
    linkTitle: string
    tncURI: string
    privacyURI: string
    privacyTitle: string
  }
  otpLength: number
  phoneNumber?: number
  resend?: {
    enabled: boolean
    title?: string
    durationInSec?: number
    resendCount?: number
  }
  retryCount: number
  error?: string | null
}

/**
 * Class to transform the props for OTPVerifyScreen.
 * It normalizes the props structure, ensuring that the screen
 * receives props in a consistent format.
 */
export class OTPVerifyScreenTransformer {
  /**
   * Transforms the input props into a standardized format.
   * This method ensures that all necessary fields are provided with default values
   * if not explicitly specified in the incoming props, making use of additional metadata
   * for fallbacks where appropriate.
   *
   * @param props The props to be transformed, of type OTPVerifyScreenProps.
   * @returns An object of type OTPTransformedProps, with a consistent and standardized structure.
   */
  static transform(props: OTPVerifyScreenProps): OTPTransformedProps {
    let phoneNumber = props?.layout?.phoneNumber ?? props?.metadata?.phoneNumber
    if (props?.maskNumber) {
      phoneNumber = phoneNumber?.toString()?.split('')
      phoneNumber?.splice(2, 5, '*****')
      phoneNumber = phoneNumber?.join('')
    }

    return {
      // Use the title from props, or default to 'Verification'
      title:
        props?.layout?.title ||
        brandsI18n.t('axis.otpCCTitle') ||
        'Verification',
      // Construct the description using the phoneNumber from props or metadata, or default to a generic message
      description: `A one-time password has been sent to ${
        phoneNumber || 'your number'
      }. Please enter the OTP below.`,
      // Transform the tncContent, providing defaults where needed
      tncContent: {
        showTnC: props?.metadata?.showTnC ?? true,
        controlled: props?.layout?.tncContent?.controlled || false,
        text:
          `${props?.layout?.tncContent?.text} ` ||
          'By clicking Submit, you are agreeing to the ',
        linkTitle:
          props?.layout?.tncContent?.linkTitle ||
          'Most Important Terms & Conditions (MITC).',
        tncURI:
          props?.layout?.tncContent?.tncURI ||
          props?.metadata?.tncUri ||
          AXIS_TNC_URI,
        privacyURI:
          props?.layout?.tncContent?.privacyURI ||
          props?.metadata?.privacyURI ||
          '',
        privacyTitle: props?.layout?.tncContent?.privacyTitle || '',
      },
      // Set otpLength from props, metadata, or default to 6
      otpLength: props?.layout?.otpLength || props?.metadata?.otpLength || 6,
      // Use phoneNumber from props or metadata
      phoneNumber: props?.layout?.phoneNumber || props?.metadata?.phoneNumber,
      // Transform the resend information, setting defaults where necessary
      resend: props?.layout?.resend
        ? {
            enabled: props?.layout?.resend?.enabled || false,
            title: props?.layout?.resend?.title || 'Resend OTP',
            durationInSec: props?.layout?.resend?.durationInSec || 30,
            resendCount: props?.layout?.resend?.resendCount,
          }
        : undefined,
      retryCount:
        props?.layout?.retryCount || props?.metadata?.remainingAttempts || 0,
      error: props?.error || null,
    }
  }
}
